import { useRef } from "react";
import { MirrorlessNote } from "@/components/MirrorlessNote";
import { useTranslation } from "@/contexts/LanguageContext";
import { Eye, Printer, Crosshair, Target, GridFour, CircleHalf } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { usePDFExport } from "@/utils/pdfExport";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.05 } },
};
const itemAnim = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ---------------------------------------------------------------------------
// Diagram: a frame showing which area the meter "reads" for each mode.
// ---------------------------------------------------------------------------
function MeterDiagram({ mode }) {
  const W = 200;
  const H = 130;
  // We draw a base 8x5 grid of cells representing the scene, then overlay
  // a highlight shape that shows where the meter pays attention.
  const cells = [];
  for (let y = 0; y < 5; y++) {
    for (let x = 0; x < 8; x++) {
      cells.push({ x: x * (W / 8), y: y * (H / 5) });
    }
  }
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block">
      {/* Frame bg */}
      <rect x="0" y="0" width={W} height={H} fill="#0A0A0A" />
      {/* Grid cells */}
      {cells.map((c, i) => (
        <rect
          key={i}
          x={c.x}
          y={c.y}
          width={W / 8}
          height={H / 5}
          fill="none"
          stroke="#27272A"
          strokeWidth="0.5"
        />
      ))}

      {/* Mode overlays */}
      {mode === "matrix" &&
        cells.map((c, i) => (
          <rect
            key={`m-${i}`}
            x={c.x + 1}
            y={c.y + 1}
            width={W / 8 - 2}
            height={H / 5 - 2}
            fill="#F5A524"
            opacity={0.5 + 0.4 * Math.random()}
          />
        ))}

      {mode === "center" && (
        <>
          <radialGradient id="cw" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F5A524" stopOpacity="0.95" />
            <stop offset="40%" stopColor="#F5A524" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#F5A524" stopOpacity="0.05" />
          </radialGradient>
          <ellipse cx={W / 2} cy={H / 2} rx={W * 0.42} ry={H * 0.55} fill="url(#cw)" />
        </>
      )}

      {mode === "partial" && (
        <circle cx={W / 2} cy={H / 2} r={H * 0.27} fill="#F5A524" opacity="0.85" />
      )}

      {mode === "spot" && (
        <circle cx={W / 2} cy={H / 2} r={H * 0.1} fill="#F5A524" opacity="0.95" />
      )}

      {/* Crosshair */}
      <line x1={W / 2 - 8} y1={H / 2} x2={W / 2 + 8} y2={H / 2} stroke="white" strokeWidth="0.7" />
      <line x1={W / 2} y1={H / 2 - 8} x2={W / 2} y2={H / 2 + 8} stroke="white" strokeWidth="0.7" />

      {/* Frame outline */}
      <rect x="1" y="1" width={W - 2} height={H - 2} fill="none" stroke="#52525B" strokeWidth="1.2" />
    </svg>
  );
}

const CONTENT = {
  en: {
    page_title: "Metering Modes: How Your Camera Measures Light",
    page_sub:
      "Before the camera picks an exposure, it measures the light in the scene. Different metering modes look at different parts of the frame. Learn the 4 standard modes — and when to use each — across Canon, Nikon, Sony, and Fujifilm.",
    download: "Download as PDF",
    generating: "Generating...",

    why_title: "What metering does",
    why_body:
      "Your camera contains a tiny light sensor (sometimes the main sensor itself). When you half-press the shutter in Aperture Priority, Shutter Priority, or Program mode, the camera measures the light, calculates how much exposure is needed, and dials in the missing setting automatically. The metering MODE tells the camera WHICH part of the frame to pay attention to. Picking the right mode is the difference between a perfectly exposed face and a silhouette.",
    why_body_2:
      "Every brand assumes the average photo should reflect 18% middle gray. If you point your camera at pure white snow, the meter sees 'too bright' and darkens the exposure — your snow becomes muddy gray. Point it at black pavement and the meter sees 'too dark' and brightens — your pavement becomes washed-out gray. Knowing the metering mode tells you what part of the frame is fooling the meter.",

    modes_title: "The 4 standard metering modes",
    modes: [
      {
        key: "matrix",
        title: "Matrix / Evaluative / Multi-segment",
        brands: "Nikon: Matrix · Canon: Evaluative · Sony / Fuji: Multi-segment",
        body: "The smart default. The camera divides the frame into 64–2000+ zones (depending on the body), reads brightness in each, compares to a database of millions of reference scenes, and computes a balanced exposure.",
        use: "General use, landscapes, evenly lit scenes, anytime you cannot anticipate the lighting.",
        warning: "Can underexpose backlit subjects (face goes dark) and can be fooled by very bright or very dark backgrounds.",
      },
      {
        key: "center",
        title: "Center-Weighted Average",
        brands: "Same name on Canon, Nikon, Sony, Fuji",
        body: "The meter reads the entire frame but gives ~60–80% of the weight to the center circle. Edges still contribute a little.",
        use: "Centered portraits, classic compositions where the subject is in the middle, scenes with a bright sky you want to partially ignore.",
        warning: "Less smart than Matrix for unusual compositions. Recompose-and-lock-exposure (AE-L button) is your friend.",
      },
      {
        key: "partial",
        title: "Partial",
        brands: "Canon-specific (also found on a few older Pentax)",
        body: "Measures roughly the center 6–10% of the frame — bigger than Spot, smaller than Center-Weighted.",
        use: "Backlit subjects where the face still fills a decent area. A practical compromise when Spot is too narrow to track.",
        warning: "Not available on Nikon / Sony / Fuji — use Spot instead on those bodies.",
      },
      {
        key: "spot",
        title: "Spot",
        brands: "Same name on Canon, Nikon, Sony, Fuji",
        body: "Measures only a tiny circle — typically 1.5–3% of the frame. On most bodies, the spot follows the active autofocus point.",
        use: "High-contrast scenes (concert performer under a spotlight, backlit faces, the moon against a dark sky). Spot-meter on the bright subject and let the dark background go black.",
        warning: "Easy to get wildly wrong exposures if the spot lands on the wrong tone. Pair with Exposure Compensation for fine-tuning.",
      },
    ],

    workflow_title: "The pro workflow — meter → check histogram → compensate",
    workflow_steps: [
      "Pick the metering mode that matches the scene (Matrix as default; Spot for high-contrast or backlit).",
      "Half-press the shutter to lock the meter reading.",
      "Glance at the histogram (see The Histogram lesson) — is the data piled against either wall?",
      "If yes, dial in Exposure Compensation (+ for brighter, − for darker) until the histogram looks right for the scene.",
      "Take the shot. Confirm on playback that the histogram and the Blinkies look good.",
    ],

    hardware_title: "How to change metering mode — by brand",
    hardware: [
      {
        name: "Canon",
        body: "Press the dedicated button labeled with the [✱] or 'metering mode' icon on the top deck (varies by body). On lower bodies, access via Q menu → Metering. Cycle: Evaluative → Partial → Spot → Center-Weighted.",
      },
      {
        name: "Nikon",
        body: "Press the metering mode button (often near the shutter release, labeled with a small frame icon) and rotate the main command dial. On lower bodies: i-Menu → Metering. Cycle: Matrix → Center-Weighted → Spot → Highlight-Weighted (newer bodies).",
      },
      {
        name: "Sony",
        body: "Press the Fn button to open the Function menu → choose Metering Mode. Or assign metering to a custom button via Menu → Setup → Custom Key Settings. Cycle: Multi → Center → Spot (Standard or Large) → Entire Screen Avg → Highlight.",
      },
      {
        name: "Fujifilm",
        body: "Many bodies have a dedicated 'Photometry' lever on the top plate, or access via Q menu → Photometry. Cycle: Multi → Spot → Average → Center-Weighted.",
      },
    ],

    common_mistakes_title: "Common student mistakes",
    common_mistakes: [
      "Leaving the camera in Spot metering after a concert shoot, then wondering why next morning's landscape is wildly under- or over-exposed. Reset to Matrix between sessions.",
      "Using Matrix on a backlit portrait and being surprised when the face is dark. Switch to Spot, meter on the face, then recompose.",
      "Trusting the meter on pure-white or pure-black scenes. The meter ALWAYS wants 18% gray — you must compensate manually.",
      "Forgetting that on most bodies, Spot follows the active autofocus point. If your AF point is on the sky, you are spot-metering the sky, not your subject.",
    ],

    pro_tip:
      "Pro tip: 80% of working pros use Matrix/Evaluative for almost every shot and rely on Exposure Compensation to fix the camera's mistakes. Spot is reserved for tough scenes. Master Matrix + Exposure Compensation first, then graduate to Spot for precision work.",
  },

  es: {
    page_title: "Modos de Medición: Como Tu Cámara Mide la Luz",
    page_sub:
      "Antes de elegir una exposición, la cámara mide la luz en la escena. Los distintos modos de medición observan partes distintas del encuadre. Aprende los 4 modos estándar — y cuando usar cada uno — en Canon, Nikon, Sony y Fujifilm.",
    download: "Descargar como PDF",
    generating: "Generando...",

    why_title: "Que hace la medición",
    why_body:
      "Tu cámara contiene un pequeño sensor de luz (a veces el propio sensor principal). Al presionar el disparador a medio recorrido en Prioridad Apertura, Prioridad Obturación o Programa, la cámara mide la luz, calcula la exposición necesaria y ajusta el parámetro faltante automáticamente. El MODO de medición le dice a la cámara EN QUE parte del encuadre poner atención. Elegir el modo correcto es la diferencia entre un rostro bien expuesto y una silueta.",
    why_body_2:
      "Cada marca asume que la foto promedio debe reflejar un gris medio del 18%. Si apuntas a nieve blanca pura, el medidor ve 'muy brillante' y oscurece la exposición — tu nieve queda gris sucia. Si apuntas a pavimento negro, el medidor ve 'muy oscuro' y aclara — tu pavimento queda gris lavado. Conocer el modo de medición te dice que parte del encuadre esta engañando al medidor.",

    modes_title: "Los 4 modos de medición estándar",
    modes: [
      {
        key: "matrix",
        title: "Matrix / Evaluativa / Multi-segmento",
        brands: "Nikon: Matrix · Canon: Evaluativa · Sony / Fuji: Multi-segmento",
        body: "El predeterminado inteligente. La cámara divide el encuadre en 64–2000+ zonas (según el cuerpo), lee el brillo en cada una, lo compara con una base de datos de millones de escenas y calcula una exposición equilibrada.",
        use: "Uso general, paisajes, escenas con luz pareja, cuando no puedes anticipar la iluminación.",
        warning: "Puede subexponer sujetos a contraluz (el rostro se va oscuro) y puede dejarse engañar por fondos muy brillantes o muy oscuros.",
      },
      {
        key: "center",
        title: "Promedio con preponderancia central",
        brands: "Mismo nombre en Canon, Nikon, Sony, Fuji",
        body: "Lee todo el encuadre pero pondera ~60–80% el círculo central. Los bordes siguen aportando un poco.",
        use: "Retratos centrados, composiciones clásicas con el sujeto al centro, escenas con cielo brillante que quieres ignorar parcialmente.",
        warning: "Menos inteligente que Matrix para composiciones inusuales. Recomponer y bloquear la exposición (botón AE-L) es tu amigo.",
      },
      {
        key: "partial",
        title: "Parcial",
        brands: "Específico de Canon (también en algunas Pentax antiguas)",
        body: "Mide aproximadamente el 6–10% central del encuadre — más grande que Spot, más pequeño que Promedio Central.",
        use: "Sujetos a contraluz donde el rostro ocupa un area razonable. Compromiso práctico cuando Spot es demasiado estrecho.",
        warning: "No disponible en Nikon / Sony / Fuji — usa Spot en esos cuerpos.",
      },
      {
        key: "spot",
        title: "Puntual (Spot)",
        brands: "Mismo nombre en Canon, Nikon, Sony, Fuji",
        body: "Mide solo un círculo diminuto — normalmente 1.5–3% del encuadre. En la mayoría de los cuerpos, el spot sigue al punto de enfoque activo.",
        use: "Escenas de alto contraste (un cantante bajo un reflector, rostros a contraluz, la luna contra cielo oscuro). Mide con spot al sujeto brillante y deja que el fondo se ennegrezca.",
        warning: "Es fácil obtener exposiciones erroneas si el spot cae en el tono equivocado. Combina con Compensación de Exposición para ajustes finos.",
      },
    ],

    workflow_title: "El flujo profesional — medir → revisar histograma → compensar",
    workflow_steps: [
      "Elige el modo de medición que coincida con la escena (Matrix por defecto; Spot para alto contraste o contraluz).",
      "Presiona el disparador a medio recorrido para fijar la lectura.",
      "Mira el histograma (ver lección El Histograma) — ¿hay datos acumulados contra algún muro?",
      "Si si, aplica Compensación de Exposición (+ para más brillo, − para menos) hasta que el histograma luzca correcto para la escena.",
      "Dispara. Confirma en reproducción que el histograma y los Blinkies están bien.",
    ],

    hardware_title: "Como cambiar el modo de medición — por marca",
    hardware: [
      {
        name: "Canon",
        body: "Pulsa el botón dedicado con el icono [✱] o de 'modo de medición' en la cubierta superior (varia según modelo). En cuerpos básicos, accede vía menú Q → Medición. Ciclo: Evaluativa → Parcial → Spot → Promedio Central.",
      },
      {
        name: "Nikon",
        body: "Pulsa el botón de modo de medición (suele estar cerca del disparador, con un pequeño icono de marco) y gira el dial principal. En cuerpos básicos: menú i → Medición. Ciclo: Matrix → Promedio Central → Spot → Highlight-Weighted (cuerpos nuevos).",
      },
      {
        name: "Sony",
        body: "Pulsa Fn para abrir el menú de funciones → elige Modo de Medición. O asigna medición a un botón personalizado en Menú → Configuración → Custom Key Settings. Ciclo: Multi → Centro → Spot (Estándar o Grande) → Promedio Pantalla Completa → Highlight.",
      },
      {
        name: "Fujifilm",
        body: "Muchos cuerpos tienen una palanca dedicada 'Photometry' en la parte superior, o accede vía menú Q → Photometry. Ciclo: Multi → Spot → Promedio → Promedio Central.",
      },
    ],

    common_mistakes_title: "Errores comunes del estudiante",
    common_mistakes: [
      "Dejar la cámara en Spot después de una sesión de concierto y al día siguiente preguntarse por que el paisaje queda sobre o subexpuesto. Resetea a Matrix entre sesiones.",
      "Usar Matrix en un retrato a contraluz y sorprenderse de que el rostro queda oscuro. Cambia a Spot, mide sobre el rostro, recompon.",
      "Confiar en el medidor en escenas de blanco puro o negro puro. El medidor SIEMPRE quiere 18% gris — debes compensar manualmente.",
      "Olvidar que en la mayoría de los cuerpos, Spot sigue al punto de enfoque activo. Si tu punto AF esta sobre el cielo, estas midiendo el cielo, no a tu sujeto.",
    ],

    pro_tip:
      "Tip pro: el 80% de los profesionales trabaja en Matrix/Evaluativa casi siempre y usa Compensación de Exposición para corregir los errores de la cámara. Spot se reserva para escenas difíciles. Domina primero Matrix + Compensación, luego graduate a Spot para trabajo de precisión.",
  },
};

const MODE_ICON = { matrix: GridFour, center: CircleHalf, partial: Target, spot: Crosshair };

export default function MeteringModesPage() {
  const { lang } = useTranslation();
  const c = CONTENT[lang] || CONTENT.en;
  const printRef = useRef(null);
  const { ready, generating, download } = usePDFExport(
    printRef,
    () => `metering-modes-${lang}.pdf`,
    [lang]
  );

  return (
    <div
      className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:bg-white print:text-black"
      data-testid="metering-page"
    >
      <div className="print:hidden">
        <motion.div variants={container} initial="hidden" animate="show" className="space-y-8">
          {/* Header */}
          <motion.div variants={itemAnim} className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
                {c.page_title}
              </h1>
              <p className="text-[#71717A] text-base max-w-3xl">{c.page_sub}</p>
            </div>
            <Button
              onClick={download}
              disabled={!ready}
              className="bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium disabled:opacity-60"
              data-testid="metering-download-btn"
            >
              <Printer className="w-4 h-4 mr-1.5" />
              {generating ? c.generating : c.download}
            </Button>
          </motion.div>
          <motion.div variants={itemAnim}><MirrorlessNote topic="metering" /></motion.div>

          {/* Why */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-2">
              <Eye weight="fill" className="w-5 h-5 text-[#F5A524]" />
              <h2 className="font-['Outfit'] text-white text-xl font-medium">{c.why_title}</h2>
            </div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed mb-3">{c.why_body}</p>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">{c.why_body_2}</p>
          </motion.div>

          {/* The 4 modes */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-4">{c.modes_title}</h2>
            <div className="space-y-5">
              {c.modes.map((m, i) => {
                const Icon = MODE_ICON[m.key];
                return (
                  <div
                    key={m.key}
                    className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4"
                    data-testid={`metering-mode-${m.key}`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-1">
                        <div className="bg-[#000] border border-white/10 rounded-lg p-2 mb-2">
                          <MeterDiagram mode={m.key} />
                        </div>
                        <p className="text-[#F5A524] text-[10px] uppercase tracking-widest font-semibold text-center font-['JetBrains_Mono']">
                          {m.brands}
                        </p>
                      </div>
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-2 mb-2">
                          {Icon && <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />}
                          <h3 className="text-white font-medium text-base">
                            {i + 1}. {m.title}
                          </h3>
                        </div>
                        <p className="text-[#A1A1AA] text-sm leading-relaxed mb-2">{m.body}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                          <div className="bg-emerald-950/30 border border-emerald-500/20 rounded p-2">
                            <p className="text-emerald-400 text-[10px] uppercase tracking-widest font-semibold mb-1">When to use</p>
                            <p className="text-[#D4D4D8] text-xs leading-relaxed">{m.use}</p>
                          </div>
                          <div className="bg-amber-950/30 border border-amber-500/20 rounded p-2">
                            <p className="text-amber-400 text-[10px] uppercase tracking-widest font-semibold mb-1">Watch out</p>
                            <p className="text-[#D4D4D8] text-xs leading-relaxed">{m.warning}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Workflow */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-[#F5A524]/40 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.workflow_title}</h2>
            <ol className="space-y-2">
              {c.workflow_steps.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#F5A524]/20 text-[#F5A524] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>
          </motion.div>

          {/* Hardware by brand */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-4">{c.hardware_title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {c.hardware.map((b, i) => (
                <div
                  key={i}
                  className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3"
                  data-testid={`metering-hw-${b.name.toLowerCase()}`}
                >
                  <p className="text-[#F5A524] font-medium text-sm mb-1.5">{b.name}</p>
                  <p className="text-[#A1A1AA] text-xs leading-relaxed">{b.body}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Common mistakes */}
          <motion.div variants={itemAnim} className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-6">
            <h2 className="font-['Outfit'] text-white text-xl font-medium mb-3">{c.common_mistakes_title}</h2>
            <ul className="space-y-2">
              {c.common_mistakes.map((m, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="mt-2 w-1 h-1 rounded-full bg-[#F5A524] shrink-0" />
                  <span className="text-[#A1A1AA] text-sm leading-relaxed">{m}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div variants={itemAnim} className="bg-[#F5A524]/10 border border-[#F5A524]/30 rounded-xl p-4">
            <p className="text-[#F5A524] text-sm leading-relaxed italic">{c.pro_tip}</p>
          </motion.div>
        </motion.div>
      </div>

      {/* Print */}
      <div ref={printRef} className="hidden print:block bg-white text-black p-4" data-testid="metering-print-view">
        <h1 className="text-3xl font-bold text-center mb-1">{c.page_title}</h1>
        <p className="text-center text-gray-500 mb-5 text-sm">{c.page_sub}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">1. {c.why_title}</h2>
        <p className="text-sm text-gray-700 mb-2">{c.why_body}</p>
        <p className="text-sm text-gray-700 mb-5">{c.why_body_2}</p>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">2. {c.modes_title}</h2>
        {c.modes.map((m, i) => (
          <div key={m.key} className="mb-4 break-inside-avoid">
            <p className="font-bold text-sm">{i + 1}. {m.title}</p>
            <p className="text-xs text-gray-500 italic mb-1">{m.brands}</p>
            <p className="text-xs text-gray-700 mb-1">{m.body}</p>
            <p className="text-xs text-gray-700"><strong>When to use:</strong> {m.use}</p>
            <p className="text-xs text-gray-700"><strong>Watch out:</strong> {m.warning}</p>
          </div>
        ))}
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">3. {c.workflow_title}</h2>
        <ol className="list-decimal ml-5 space-y-1 mb-4">
          {c.workflow_steps.map((s, i) => (<li key={i} className="text-xs text-gray-700">{s}</li>))}
        </ol>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">4. {c.hardware_title}</h2>
        <table className="w-full text-xs border-collapse mb-4">
          <tbody>
            {c.hardware.map((b, i) => (
              <tr key={i}><td className="border border-gray-300 p-2 font-semibold w-1/5">{b.name}</td><td className="border border-gray-300 p-2">{b.body}</td></tr>
            ))}
          </tbody>
        </table>
        <h2 className="text-xl font-bold mb-2 border-b-2 border-gray-200 pb-2">5. {c.common_mistakes_title}</h2>
        <ul className="list-disc ml-5 space-y-1 mb-4">
          {c.common_mistakes.map((m, i) => (<li key={i} className="text-xs text-gray-700">{m}</li>))}
        </ul>
        <p className="text-xs text-gray-700 italic"><strong>Pro tip:</strong> {c.pro_tip}</p>
        <p className="text-center text-xs text-gray-400 mt-4">www.dslradvisor.com</p>
      </div>
    </div>
  );
}
